import type { Row } from '../../../core/view-manager/row.js';
import type { BarState } from '../extensions/types.js';
import type { GanttSingleView } from '../gantt-view-manager.js';

export type RowClassification = 'scheduled' | 'unscheduled' | 'invalid-range';

export interface GanttRowData {
  readonly row: Row;
  readonly rowIndex: number;
  readonly state: BarState | null;
  readonly classification: RowClassification;
  /** Normalised progress in [0, 1] — `null` when no progress column is set. */
  readonly progress: number | null;
}

/**
 * Decoration layer emitted by a {@link BarDecorator}. Renderer applies
 * each field on top of the Standard bar render without re-positioning.
 */
export interface GanttBarDecoration {
  readonly classList?: readonly string[];
  readonly inlineStyles?: Readonly<Record<string, string>>;
  /** Inline badges (icons / tiny text) stacked at the bar's end edge. */
  readonly badges?: readonly string[];
}

export class RowValidator {
  static classify(state: BarState | null): RowClassification {
    if (!state) return 'unscheduled';
    if (state.startMs > state.endMs) return 'invalid-range';
    return 'scheduled';
  }
}

export function readProgress(view: GanttSingleView, row: Row): number | null {
  const col = view.progressCol$.value;
  if (!col) return null;
  const cell = view.cellGetOrCreate(row.rowId, col);
  const raw = cell.jsonValue$.value;
  if (typeof raw !== 'number' || Number.isNaN(raw)) return null;
  // Accept either 0-1 or 0-100 — clamp to 0-1 for rendering.
  const normalised = raw > 1 ? raw / 100 : raw;
  return Math.max(0, Math.min(1, normalised));
}
